import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import api from "../api";
import "./Table.css";
import "./PayrollPeriodDetail.css";

const fmt  = n => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = d => d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtDT = d => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : null;

const LEAD_LABELS = {
  self_generated:   "Self Generated",
  partner_referral: "Partner Referral",
  client_referral:  "Client Referral",
  company_referral: "Company Referral",
};

const STATUS_LABEL = {
  future:  "Future",
  current: "Current",
  paid:    "Paid",
};

const STMT_LABEL = {
  draft:                   "Draft",
  admin_review:            "Admin Review",
  manager_review:          "Manager Review",
  lo_review:               "Awaiting Your Approval",
  lo_disputed:             "Disputed",
  dispute_manager_review:  "Dispute — Manager Review",
  dispute_admin_review:    "Dispute — Admin Review",
  approved:                "Approved",
  paid:                    "Paid",
};

// ── Loans mini-table ────────────────────────────────────────────────────────
function LoansTable({ loans }) {
  if (!loans || loans.length === 0) {
    return <p className="empty-row" style={{ padding: "16px" }}>No loans in this period.</p>;
  }
  return (
    <table className="data-table loans-subtable">
      <thead>
        <tr>
          <th>Loan #</th>
          <th>Borrower</th>
          <th>Amount</th>
          <th>Lead Source</th>
          <th>Commission ($)</th>
          <th>Commission (bps)</th>
          <th>Exception ($)</th>
          <th>Funded</th>
        </tr>
      </thead>
      <tbody>
        {loans.map(l => (
          <tr key={l.loan_id}>
            <td className="mono">{l.loan_number}</td>
            <td>{l.borrower_last_name}</td>
            <td className="mono">{fmt(l.loan_amount)}</td>
            <td>{LEAD_LABELS[l.lead_source] || l.lead_source || "—"}</td>
            <td className="mono" style={{ color: "var(--success)" }}>{l.commission_amount != null ? fmt(l.commission_amount) : "—"}</td>
            <td className="mono">{l.commission_bps != null ? l.commission_bps : "—"}</td>
            <td className="mono">{l.exception_amount_dollars != null ? fmt(l.exception_amount_dollars) : "—"}</td>
            <td>{fmtD(l.funding_date)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Statement card (LO view) ────────────────────────────────────────────────
function StatementCard({ stmt, onAction, actionLoading }) {
  const [comment, setComment] = useState("");
  const [showDispute, setShowDispute] = useState(false);

  if (!stmt) return null;

  const isLOReview  = stmt.status === "lo_review";
  const isDisputed  = stmt.status === "lo_disputed";
  const isApproved  = stmt.status === "approved" || stmt.status === "paid";

  const steps = [
    { done: !!stmt.admin_submitted_at, label: "Admin submitted",  detail: stmt.admin_submitted_at ? fmtDT(stmt.admin_submitted_at) : null },
    { done: !!stmt.manager_at,         label: "Manager approved", detail: stmt.manager_at ? `${fmtDT(stmt.manager_at)} · ${stmt.manager_comment || ""}` : null },
    { done: !!stmt.admin_lo_sent_at,   label: "Sent to you",      detail: stmt.admin_lo_sent_at ? fmtDT(stmt.admin_lo_sent_at) : null },
    { done: !!stmt.lo_at,              label: stmt.lo_decision === "approved" ? "You approved" : "You disputed",
      detail: stmt.lo_at ? `${fmtDT(stmt.lo_at)}${stmt.lo_comment ? " · " + stmt.lo_comment : ""}` : null },
  ];

  if (stmt.dispute_manager_at) {
    steps.push({ done: true, label: "Manager reviewed dispute", detail: `${fmtDT(stmt.dispute_manager_at)} · ${stmt.dispute_manager_comment || ""}` });
  }
  if (stmt.dispute_admin_at) {
    steps.push({ done: true, label: "Admin resolved dispute", detail: `${fmtDT(stmt.dispute_admin_at)} · ${stmt.dispute_admin_comment || ""}` });
  }

  return (
    <div className="statement-card">
      <h3 className="statement-title">Commission Statement</h3>
      <div className={`statement-status-badge badge-stmt-${stmt.status.replace(/_/g, "-")}`}>
        {STMT_LABEL[stmt.status] || stmt.status}
      </div>

      {/* Step timeline */}
      <div className="stmt-timeline">
        {steps.map((s, i) => (
          <div key={i} className={`stmt-step ${s.done ? "done" : "pending"}`}>
            <div className="step-dot" />
            <div className="step-content">
              <span className="step-label">{s.label}</span>
              {s.detail && <span className="step-detail">{s.detail}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* LO actions */}
      {isLOReview && (
        <div className="stmt-actions">
          <button
            className="btn-primary btn-sm"
            style={{ background: "var(--success)", borderColor: "var(--success)" }}
            disabled={actionLoading}
            onClick={() => onAction("approve", "")}
          >
            Approve Statement
          </button>
          <button
            className="btn-secondary btn-sm"
            style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
            disabled={actionLoading}
            onClick={() => setShowDispute(v => !v)}
          >
            Dispute
          </button>
        </div>
      )}

      {isLOReview && showDispute && (
        <div className="dispute-form">
          <label>Describe the issue:</label>
          <textarea
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Explain what needs to be corrected..."
          />
          <button
            className="btn-primary btn-sm"
            style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
            disabled={actionLoading || !comment.trim()}
            onClick={() => onAction("dispute", comment)}
          >
            Submit Dispute
          </button>
        </div>
      )}

      {isDisputed && (
        <div className="stmt-info-box">
          Your dispute has been submitted. An admin and your manager will review it.
        </div>
      )}

      {isApproved && (
        <div className="stmt-info-box approved">
          Statement approved.
        </div>
      )}
    </div>
  );
}

// ── Admin statement row ─────────────────────────────────────────────────────
function AdminStatementRow({ lo, onAction, actionLoading, periodId }) {
  const [comment, setComment] = useState("");
  const [showResolve, setShowResolve] = useState(false);
  const stmtId = lo.statement_id;
  const status = lo.statement_status;

  const actionBtn = () => {
    if (!stmtId) return null;
    if (status === "draft" || !status) {
      return <button className="btn-primary btn-sm" disabled={actionLoading} onClick={() => onAction(stmtId, "submit")}>Submit to Manager</button>;
    }
    if (status === "admin_review") {
      return <button className="btn-primary btn-sm" disabled={actionLoading} onClick={() => onAction(stmtId, "send-to-lo")}>Send to LO</button>;
    }
    if (status === "lo_disputed") {
      return <button className="btn-secondary btn-sm" disabled={actionLoading} onClick={() => onAction(stmtId, "dispute-manager")}>Escalate to Manager</button>;
    }
    if (status === "dispute_admin_review") {
      return (
        <button className="btn-secondary btn-sm" disabled={actionLoading} onClick={() => setShowResolve(v => !v)}>
          Resolve Dispute
        </button>
      );
    }
    return null;
  };

  const BADGE = {
    draft:                   "badge-stmt-draft",
    admin_review:            "badge-stmt-admin-review",
    manager_review:          "badge-stmt-manager-review",
    lo_review:               "badge-stmt-lo-review",
    lo_disputed:             "badge-stmt-lo-disputed",
    dispute_manager_review:  "badge-stmt-dispute-manager-review",
    dispute_admin_review:    "badge-stmt-dispute-admin-review",
    approved:                "badge-stmt-approved",
    paid:                    "badge-stmt-paid",
  };

  return (
    <div className="admin-stmt-row">
      <div className="admin-stmt-info">
        <span className="admin-stmt-name">{lo.loan_officer_name}</span>
        <span className="admin-stmt-meta">{lo.units} loans · {fmt(lo.volume)} volume · {fmt(lo.commission)} commission</span>
      </div>
      <div className="admin-stmt-right">
        {status && (
          <span className={`badge badge-stmt ${BADGE[status] || ""}`}>
            {STMT_LABEL[status] || status}
          </span>
        )}
        {actionBtn()}
      </div>
      {showResolve && (
        <div className="dispute-form" style={{ gridColumn: "1/-1" }}>
          <label>Resolution note:</label>
          <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} />
          <button className="btn-primary btn-sm" disabled={actionLoading || !comment.trim()} onClick={() => { onAction(stmtId, "dispute-admin", comment); setShowResolve(false); }}>
            Resolve & Resend to LO
          </button>
        </div>
      )}
    </div>
  );
}

// ── Manager statement row ───────────────────────────────────────────────────
function ManagerLORow({ lo, onAction, actionLoading }) {
  const [expanded, setExpanded] = useState(false);
  const stmtId = lo.statement_id;
  const status = lo.statement_status;

  return (
    <div className="manager-lo-row">
      <div className="manager-lo-header" onClick={() => setExpanded(v => !v)}>
        <span className="expand-arrow">{expanded ? "▾" : "▸"}</span>
        <span className="manager-lo-name">{lo.loan_officer_name}</span>
        <span className="manager-lo-stats">
          {lo.units} loans &nbsp;·&nbsp; {fmt(lo.volume)} &nbsp;·&nbsp; {fmt(lo.commission)}
        </span>
        <div className="manager-lo-right">
          {status && (
            <span className={`badge badge-stmt`} style={{ marginRight: 8 }}>
              {STMT_LABEL[status] || status}
            </span>
          )}
          {status === "manager_review" && (
            <button className="btn-primary btn-sm" disabled={actionLoading} onClick={e => { e.stopPropagation(); onAction(stmtId, "approve"); }}>
              Approve
            </button>
          )}
          {status === "dispute_manager_review" && (
            <button className="btn-secondary btn-sm" style={{ color: "var(--danger)", borderColor: "var(--danger)" }} disabled={actionLoading} onClick={e => { e.stopPropagation(); onAction(stmtId, "dispute-decision"); }}>
              Resolve Dispute
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="manager-lo-loans">
          <LoansTable loans={lo.loans} />
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function PayrollPeriodDetail() {
  const { periodId } = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const [detail, setDetail]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]             = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);

  const isAdmin   = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isLO      = user?.role === "loan_officer";

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(`/payroll/periods/${periodId}`)
      .then(r => setDetail(r.data))
      .catch(err => setError(err.response?.data?.detail || "Failed to load period"))
      .finally(() => setLoading(false));
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  // LO action: approve or dispute
  const handleLOAction = async (action, comment) => {
    const stmt = detail?.my_statement;
    if (!stmt) return;
    setActionLoading(true);
    try {
      await api.post(`/payroll/statements/${stmt.id}/lo-response`, {
        decision: action === "approve" ? "approved" : "disputed",
        comment: comment || null,
      });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Admin/manager actions
  const handleAdminAction = async (stmtId, action, comment) => {
    setActionLoading(true);
    try {
      if (action === "submit") {
        await api.post(`/payroll/statements/${stmtId}/submit`);
      } else if (action === "send-to-lo") {
        await api.post(`/payroll/statements/${stmtId}/send-to-lo`);
      } else if (action === "dispute-manager") {
        await api.post(`/payroll/statements/${stmtId}/dispute-manager`);
      } else if (action === "dispute-admin") {
        await api.post(`/payroll/statements/${stmtId}/dispute-admin`, { decision: "resolved", comment });
      }
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Manager actions
  const handleManagerAction = async (stmtId, action) => {
    setActionLoading(true);
    try {
      if (action === "approve") {
        await api.post(`/payroll/statements/${stmtId}/manager-review`, { decision: "approved" });
      } else if (action === "dispute-decision") {
        await api.post(`/payroll/statements/${stmtId}/dispute-manager-decision`, { decision: "reviewed" });
      }
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!window.confirm("Mark this entire pay period as Paid? This will also mark all approved statements as paid.")) return;
    setMarkingPaid(true);
    try {
      await api.post(`/payroll/periods/${periodId}/mark-paid`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  if (loading) return <div className="loading">Loading period...</div>;
  if (error)   return <div className="error-banner" style={{ margin: 24 }}>{error}</div>;
  if (!detail) return null;

  const { period, my_loans, my_statement, lo_rows } = detail;

  const canMarkPaid = isAdmin &&
    period.status !== "paid" &&
    lo_rows?.length > 0 &&
    lo_rows.every(r => r.statement_status === "approved" || r.statement_status === "paid");

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <button className="back-btn" onClick={() => navigate("/commissions")}>← Pay Periods</button>
          <h1 className="page-title">
            {new Date(period.period_start + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric" })}
            {" – "}
            {new Date(period.period_end + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </h1>
          <p className="page-subtitle">
            Pay date: {period.pay_date ? new Date(period.pay_date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "TBD"}
            {" · "}
            <span className={`badge badge-period-${period.status}`} style={{ fontSize: "0.75rem" }}>
              {STATUS_LABEL[period.status] || period.status}
            </span>
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 8 }}>
            {canMarkPaid && (
              <button className="btn-primary btn-sm" onClick={handleMarkPaid} disabled={markingPaid}>
                {markingPaid ? "Marking..." : "Mark Period as Paid"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── LO view ── */}
      {isLO && (
        <div className="period-lo-layout">
          <div className="period-loans-section">
            <h2 className="section-title">Your Loans This Period</h2>
            <div className="table-card" style={{ marginTop: 0 }}>
              <LoansTable loans={my_loans} />
            </div>
            {my_loans?.length > 0 && (
              <div className="period-totals">
                <span>{my_loans.length} loans</span>
                <span>{fmt(my_loans.reduce((s, l) => s + l.loan_amount, 0))} volume</span>
                <span style={{ color: "var(--success)" }}>{fmt(my_loans.reduce((s, l) => s + (l.commission_amount || 0), 0))} commission</span>
              </div>
            )}
          </div>
          <div className="period-stmt-section">
            <StatementCard stmt={my_statement} onAction={handleLOAction} actionLoading={actionLoading} />
          </div>
        </div>
      )}

      {/* ── Manager view ── */}
      {isManager && (
        <div>
          <h2 className="section-title">Team Production</h2>
          {(!lo_rows || lo_rows.length === 0) ? (
            <div className="table-card">
              <p className="empty-row" style={{ padding: 24 }}>No team members have loans in this period.</p>
            </div>
          ) : (
            <div className="manager-lo-list">
              {lo_rows.map(lo => (
                <ManagerLORow
                  key={lo.loan_officer_id}
                  lo={lo}
                  onAction={handleManagerAction}
                  actionLoading={actionLoading}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Admin view ── */}
      {isAdmin && (
        <div>
          <h2 className="section-title">All Loan Officers</h2>
          {(!lo_rows || lo_rows.length === 0) ? (
            <div className="table-card">
              <p className="empty-row" style={{ padding: 24 }}>No loans found in this period.</p>
            </div>
          ) : (
            <>
              <div className="admin-stmt-list">
                {lo_rows.map(lo => (
                  <AdminStatementRow
                    key={lo.loan_officer_id}
                    lo={lo}
                    onAction={handleAdminAction}
                    actionLoading={actionLoading}
                    periodId={periodId}
                  />
                ))}
              </div>
              {/* Loans detail cards */}
              {lo_rows.map(lo => (
                <div key={lo.loan_officer_id} className="admin-lo-loans">
                  <h3 className="lo-loans-title">{lo.loan_officer_name}</h3>
                  <div className="table-card" style={{ marginTop: 0 }}>
                    <LoansTable loans={lo.loans} />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
